import { Component, inject, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { CardComponent } from '@/shared/ui-components/card/card.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DividerComponent } from '@/shared/ui-components/divider/divider.component';
import { CurrencyDisplayComponent } from '@/shared/ui-components/currency-display/currency-display.component';
import { NotificationGroup, NotificationItem as WidgetNotificationItem } from '@/pages/dashboards/admin-dashboard/data-access/admin-dashboard.model';
import { RouterLink } from '@angular/router';
import { NotificationsService } from '@/pages/notifications/notifications.service';
import { NotificationItem, NotificationPriority } from '@/pages/notifications/data-access/notifications.model';
import { take } from 'rxjs';

@Component({
    standalone: true,
    selector: 'app-notifications-widget',
    imports: [ButtonModule, MenuModule, CardComponent, AngularSvgIconModule, DividerComponent, CurrencyDisplayComponent, RouterLink],
    templateUrl: './notificationswidget.html'
})
export class NotificationsWidget implements OnInit {
    private readonly _notificationsService = inject(NotificationsService);

    items = [
        { label: 'Add New', icon: 'pi pi-fw pi-plus' },
        { label: 'Remove', icon: 'pi pi-fw pi-trash' }
    ];

    notifications: NotificationGroup[] = [];

    public ngOnInit(): void {

        this.refreshNotifications();
        this._notificationsService
            .getNotificationsSummary$()
            .pipe(take(1))
            .subscribe({
                next: () => this.refreshNotifications(),
                error: () => this.refreshNotifications(),
            });
    }

    private refreshNotifications(): void {
        const recent = this._notificationsService.getRecentNotifications();
        this.notifications = this.mapRecentNotificationsToGroups(recent);
    }

    private mapRecentNotificationsToGroups(recent: NotificationItem[]): NotificationGroup[] {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfToday.getDate() - 1);
        const startOfLastWeek = new Date(startOfToday);
        startOfLastWeek.setDate(startOfToday.getDate() - 7);

        const todayItems: WidgetNotificationItem[] = [];
        const yesterdayItems: WidgetNotificationItem[] = [];
        const lastWeekItems: WidgetNotificationItem[] = [];

        for (const n of recent ?? []) {
            const createdAt = new Date(n.createdAt ?? 0);
            const item: WidgetNotificationItem = {
                icon: this.mapPriorityToWidgetIcon(n.priority),
                message: n.message || n.title || '',
            };
            
            if (createdAt >= startOfToday) {
                todayItems.push(item);
            } else if (createdAt >= startOfYesterday) {
                yesterdayItems.push(item);
            } else if (createdAt >= startOfLastWeek) {
                lastWeekItems.push(item);
            } else {
                lastWeekItems.push(item);
            }
        }

        const groups: NotificationGroup[] = [];
        if (todayItems.length) groups.push({ label: 'TODAY', items: todayItems });
        if (yesterdayItems.length) groups.push({ label: 'YESTERDAY', items: yesterdayItems });
        if (lastWeekItems.length) groups.push({ label: 'LAST WEEK', items: lastWeekItems });

        // If API returns nothing, keep the array empty (template will render nothing).
        return groups;
    }

    private mapPriorityToWidgetIcon(priority: number): string {
        switch (priority) {
            case NotificationPriority.CRITICAL:
            case NotificationPriority.HIGH:
                return 'yellow';
            case NotificationPriority.MEDIUM:
                return 'check';
            default:
                return 'blue';
        }
    }
}
